import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { submitContact, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const CONTACT_INFO = {
  email: 'sebastianrodriguezpsa@gmail.com',
  phone: '+54-380-4379609',
  location: 'Argentina',
};

const Contact = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await submitContact({
        company_name: formData.companyName,
        contact_name: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      });
      toast.success('¡Mensaje enviado! Nos pondremos en contacto pronto.');
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          message: '',
        });
      }, 4000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al enviar el mensaje');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-gradient-to-b from-sky-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sky-600 font-semibold text-sm uppercase tracking-wide mb-2 block">
            Contáctenos
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Comencemos a Trabajar Juntos
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Su empresa puede ser parte del cambio. Contáctenos para conocer cómo colaborar.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6 border-2 border-sky-100 bg-gradient-to-br from-sky-50 to-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Información de Contacto</h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">Email</div>
                      <a
                        href={`mailto:${CONTACT_INFO.email}`}
                        data-testid="contact-email-link"
                        className="text-gray-900 hover:text-sky-600 transition-colors font-medium break-all"
                      >
                        {CONTACT_INFO.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">Teléfono</div>
                      <a
                        href={`tel:${CONTACT_INFO.phone}`}
                        data-testid="contact-phone-link"
                        className="text-gray-900 hover:text-sky-600 transition-colors font-medium"
                      >
                        {CONTACT_INFO.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-1">Ubicación</div>
                      <p className="text-gray-900 font-medium">{CONTACT_INFO.location}</p>
                    </div>
                  </div>
                </div>
              </Card>

              <div className="bg-gradient-to-br from-sky-600 to-blue-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Horario de Atención</h3>
                <div className="space-y-2 text-sky-100">
                  <p>Lunes a Viernes: 9:00 - 18:00</p>
                  <p>Sábados: 9:00 - 13:00</p>
                  <p className="pt-2 border-t border-white/20">Tiempo de respuesta: 24-48 horas</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <Card className="p-8 border-2 border-gray-100">
                {isSubmitted ? (
                  <div data-testid="form-success-message" className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">¡Mensaje Enviado!</h3>
                    <p className="text-lg text-gray-600">
                      Gracias por su interés. Nos pondremos en contacto con usted pronto.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre de la Empresa *
                      </label>
                      <Input
                        data-testid="contact-company-input"
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        placeholder="Ej: Empresa S.A."
                        className="w-full border-2 border-gray-200 focus:border-sky-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre del Contacto *
                      </label>
                      <Input
                        data-testid="contact-name-input"
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        required
                        placeholder="Ej: Juan Pérez"
                        className="w-full border-2 border-gray-200 focus:border-sky-400 transition-colors"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email *
                        </label>
                        <Input
                          data-testid="contact-email-input"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="contacto@empresa.com"
                          className="w-full border-2 border-gray-200 focus:border-sky-400 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Teléfono *
                        </label>
                        <Input
                          data-testid="contact-phone-input"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="+54 11 1234-5678"
                          className="w-full border-2 border-gray-200 focus:border-sky-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mensaje *
                      </label>
                      <Textarea
                        data-testid="contact-message-input"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Cuéntenos cómo le gustaría colaborar con nuestra fundación..."
                        className="w-full border-2 border-gray-200 focus:border-sky-400 transition-colors resize-none"
                      />
                    </div>

                    <Button
                      data-testid="contact-submit-btn"
                      type="submit"
                      disabled={isLoading}
                      size="lg"
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white text-lg py-6 transition-colors group"
                    >
                      <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                      {isLoading ? 'Enviando...' : 'Enviar Mensaje'}
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      Al enviar este formulario, acepta ser contactado por nuestra fundación.
                    </p>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
